import React, { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, BookOpen } from "@phosphor-icons/react";

const CATS = ["faq", "policy", "playbook", "service", "financing", "warranty", "branch", "general"];

export default function KnowledgeBase({ embedded = false }) {
  const { t, lang } = useLang();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", category: "general", content: "", tags: "" });

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (category) params.set("category", category);
    const { data } = await api.get(`/kb?${params.toString()}`);
    setDocs(data);
  };

  useEffect(() => { load(); }, [category]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title, category: form.category, content: form.content,
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.patch(`/kb/${editing}`, payload);
        toast.success(lang === "es" ? "Actualizado" : "Updated");
      } else {
        await api.post("/kb", payload);
        toast.success(lang === "es" ? "Documento creado" : "Document created");
      }
      setOpen(false);
      setEditing(null);
      setForm({ title: "", category: "general", content: "", tags: "" });
      load();
    } catch { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "es" ? "¿Eliminar?" : "Delete?")) return;
    try {
      await api.delete(`/kb/${id}`);
      load();
    } catch { toast.error("Error"); }
  };

  const startEdit = (d) => {
    setEditing(d.id);
    setForm({ title: d.title, category: d.category, content: d.content, tags: (d.tags || []).join(", ") });
    setOpen(true);
  };

  return (
    <>
      {!embedded && (
      <Topbar
        title={t("kb.title")}
        subtitle={t("kb.subtitle")}
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({title:"",category:"general",content:"",tags:""});}}}>
            <DialogTrigger asChild>
              <Button className="gap-1.5 ml-2" data-testid="btn-new-kb"><Plus size={14}/>{t("kb.new")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? t("common.edit") : t("kb.new")}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3" data-testid="form-kb">
                <div><Label>Title</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} required data-testid="kb-title"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("kb.category")}</Label>
                    <Select value={form.category} onValueChange={(v)=>setForm({...form,category:v})}>
                      <SelectTrigger data-testid="kb-category"><SelectValue/></SelectTrigger>
                      <SelectContent>{CATS.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("kb.tags")} (,)</Label><Input value={form.tags} onChange={(e)=>setForm({...form,tags:e.target.value})} data-testid="kb-tags"/></div>
                </div>
                <div><Label>{t("kb.content")}</Label>
                  <Textarea rows={8} value={form.content} onChange={(e)=>setForm({...form,content:e.target.value})} required data-testid="kb-content"/>
                </div>
                <DialogFooter><Button type="submit" data-testid="kb-save">{t("common.save")}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      )}
      <div className={embedded ? "space-y-4 animate-fade-in" : "p-8 space-y-4 animate-fade-in flex-1 overflow-y-auto min-h-0"}>
        {embedded && (
          <div className="flex justify-end">
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({title:"",category:"general",content:"",tags:""});}}}>
              <DialogTrigger asChild>
                <Button className="gap-1.5" data-testid="btn-new-kb"><Plus size={14}/>{t("kb.new")}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>{editing ? t("common.edit") : t("kb.new")}</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-3" data-testid="form-kb">
                  <div><Label>Title</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} required data-testid="kb-title"/></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("kb.category")}</Label>
                      <Select value={form.category} onValueChange={(v)=>setForm({...form,category:v})}>
                        <SelectTrigger data-testid="kb-category"><SelectValue/></SelectTrigger>
                        <SelectContent>{CATS.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>{t("kb.tags")} (,)</Label><Input value={form.tags} onChange={(e)=>setForm({...form,tags:e.target.value})} data-testid="kb-tags"/></div>
                  </div>
                  <div><Label>{t("kb.content")}</Label>
                    <Textarea rows={8} value={form.content} onChange={(e)=>setForm({...form,content:e.target.value})} required data-testid="kb-content"/>
                  </div>
                  <DialogFooter><Button type="submit" data-testid="kb-save">{t("common.save")}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input
              className="pl-9"
              placeholder={t("common.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="kb-search"
            />
          </div>
          <Select value={category || "all"} onValueChange={(v)=>setCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="kb-filter-category"><SelectValue placeholder={t("kb.category")}/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {CATS.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="kb-refresh">{t("common.search")}</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.length === 0 && (
            <div className="col-span-full text-center py-16 text-muted-foreground border border-dashed border-border rounded-md">
              <BookOpen size={40} className="mx-auto mb-2 opacity-40"/>
              {t("common.empty")}
            </div>
          )}
          {docs.map((d) => (
            <div key={d.id} className="border border-border rounded-md p-5 bg-card hover:-translate-y-0.5 transition-all" data-testid={`kb-item-${d.id}`}>
              <div className="flex items-start justify-between mb-2">
                <Badge variant="outline" className="uppercase text-[10px]">{d.category}</Badge>
              </div>
              <h3 className="font-heading font-semibold mb-2 leading-tight">{d.title}</h3>
              <p className="text-xs text-muted-foreground line-clamp-4">{d.content}</p>
              {d.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {d.tags.map((tg) => <Badge key={tg} variant="secondary" className="text-[10px]">{tg}</Badge>)}
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => startEdit(d)} data-testid={`kb-edit-${d.id}`}>{t("common.edit")}</Button>
                <Button size="sm" variant="ghost" onClick={() => del(d.id)} data-testid={`kb-del-${d.id}`}>{t("common.delete")}</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
